'use client';
import React, { useEffect, useState } from 'react';

import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import s from './Base.module.scss';

import Sidemenu from '../../components/Sidemenu/Sidemenu';
import MobileMenu from '../../components/MobileMenu/MobileMenu';
import HeadLine from '../../components/HeadLine/HeadLine';
import Toolbar from '../../components/Toolbar/Toolbar';
import { CountryProvider } from '../../utils/country-provider';
import { SidebarProvider, useSidebar } from '../../utils/sidebar-provider';
import { BookmarkProvider } from '../../utils/bookmark-provider';
import Flag from '../../components/Flag/Flag';
import Modal from '../../components/Modal/Modal';
import Richtext from '../../components/Richtext/Richtext';
import ExpandView from '../../components/ExpandView/ExpandView';
import BookmarkDropdown from '../../components/BookmarkDropdown/BookmarkDropdown';
import SkipToContent from '../../components/SkipToContent/SkipToContent';
import LatestUpdatesModal from '../../components/LatestUpdatesModal';

const BaseContent = ({
    containerName = '',
    siteSetting = {},
    sidebarMenuItems = {},
    title = '',
    toolbar = {},
    systemUrls = {},
    supportForm = {},
    country = null,
    user = null,
    backLink = null,
    hideTitle = false,
    notificationCounts = {},
    shouldShowSidebar = true,
    bookmarkedPages = [],
    clientDashboards = [],
    Container,
    isErrorPage = false,
    ...restProps
}) => {
    const name = containerName ? containerName : Container.displayName;
    const classes = classNames(s.Root, [s[`Root--${name}`]]);
    const { isSidebarShown, toggleSidebar } = useSidebar();

    const { disclaimer } = siteSetting;
    const activeDashboard = clientDashboards.find(
        (dashboard) => dashboard.isActive
    );

    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [showLatestUpdates, setShowLatestUpdates] = useState(false);

    useEffect(() => {
        const hasSeenModal = Cookies.get('hasSeenDisclaimerModal');
        if (!hasSeenModal) {
            setShowDisclaimer(true);
        }
    }, []);

    useEffect(() => {
        const latestUpdates = siteSetting.latestUpdates;
        if (!latestUpdates?.id) return;
        const hasSeenUpdates = Cookies.get(
            `hasSeenLatestUpdates_${latestUpdates.id}`
        );
        if (!hasSeenUpdates) {
            setShowLatestUpdates(true);
        }
    }, []);

    useEffect(() => {
        if (isSidebarShown !== shouldShowSidebar) {
            toggleSidebar();
        }
    }, [shouldShowSidebar]);

    const handleModalClose = () => {
        setShowDisclaimer(false);
        Cookies.set('hasSeenDisclaimerModal', 'true');
    };

    const handleLatestUpdatesClose = () => {
        setShowLatestUpdates(false);
        Cookies.set(
            `hasSeenLatestUpdates_${siteSetting.latestUpdates?.id}`,
            'true'
        );
    };

    return (
        <>
            <div className={classes}>
                <SkipToContent />
                <div
                    className={s.Document}
                    role="document"
                    style={{
                        gridTemplateColumns: '0fr 1fr',
                    }}>
                    {!!Object.keys(sidebarMenuItems).length && (
                        <>
                            <div className={s.MobileMenu}>
                                <MobileMenu
                                    items={sidebarMenuItems}
                                    logoutUrl={systemUrls.logOut}
                                    supportForm={supportForm}
                                    user={user}
                                    disclaimer={siteSetting.disclaimer}
                                    notificationCounts={notificationCounts}
                                    secondaryLinks={siteSetting.secondaryLinks}
                                    latestUpdates={siteSetting.latestUpdates}
                                    bookmarkedPages={bookmarkedPages}
                                    clientDashboards={clientDashboards}
                                    dashboardId={activeDashboard?.id}
                                />
                            </div>
                            <aside
                                className={classNames(s.Sidebar, {
                                    [s['Sidebar--Hidden']]: !isSidebarShown,
                                })}>
                                <Sidemenu
                                    id={'vertical'}
                                    items={sidebarMenuItems}
                                    supportForm={supportForm}
                                    logoutUrl={systemUrls.logOut}
                                    secondaryLinks={siteSetting.secondaryLinks}
                                    latestUpdates={siteSetting.latestUpdates}
                                    disclaimer={siteSetting.disclaimer}
                                    notificationCounts={notificationCounts}
                                    dashboardId={activeDashboard?.id}
                                />
                            </aside>
                        </>
                    )}
                    <main className={s.Main} id="mainContent">
                        <div
                            className={classNames(s.Header, {
                                [s['Header--HideTitle']]: hideTitle,
                                [s['Header--BackLink']]: !!backLink,
                            })}>
                            {backLink ? (
                                <a className={s.BackLink} href={backLink.url}>
                                    <span className={s.BackLinkLabel}>
                                        {backLink.text}
                                    </span>
                                </a>
                            ) : (
                                title && (
                                    <div className={s.HeadLineWrapper}>
                                        {country && (
                                            <div className={s.Flag}>
                                                <Flag
                                                    countryCode={country.code}
                                                />
                                            </div>
                                        )}
                                        <HeadLine title={title} />
                                    </div>
                                )
                            )}
                            {!!user && (
                                <div className={s.Toolbar}>
                                    {!isErrorPage && <ExpandView />}
                                    <BookmarkDropdown
                                        bookmarkedPages={bookmarkedPages}
                                    />
                                    <div className={s.ToolbarWrapper}>
                                        <Toolbar
                                            user={user}
                                            logoutUrl={systemUrls.logOut}
                                            clientDashboards={clientDashboards}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <Container
                            siteSettings={siteSetting}
                            bookmarkedPages={bookmarkedPages}
                            user={user}
                            {...restProps}
                        />
                    </main>
                </div>
            </div>
            {showLatestUpdates && !!siteSetting.latestUpdates && (
                <LatestUpdatesModal
                    {...siteSetting.latestUpdates}
                    isOpen
                    onClose={handleLatestUpdatesClose}
                />
            )}
            {showDisclaimer && !!disclaimer?.modal && (
                <div className={s.DisclaimerWrapper}>
                    <Modal
                        isOpen
                        title={disclaimer.modal.title}
                        bottomButtonOnClick={handleModalClose}
                        bottomButtonContent={
                            <div className={s.DisclaimerModalButton}>
                                {disclaimer.modal.buttonText}
                                <span className="sr-only">, close modal</span>
                            </div>
                        }>
                        <div className={s.Disclaimer}>
                            <Richtext text={disclaimer.modal.content} />
                        </div>
                    </Modal>
                </div>
            )}
        </>
    );
};

BaseContent.propTypes = {
    containerName: PropTypes.string,
    Container: PropTypes.elementType.isRequired,
};

const Base = (Container, extraProps) => {
    const { isErrorPage = false } = extraProps || {};
    const Wrapper = (props) => (
        <BookmarkProvider initialBookmarks={props.bookmarkedPages}>
            <CountryProvider value={props.country}>
                <SidebarProvider shouldShowSidebar={props.shouldShowSidebar}>
                    <BaseContent
                        Container={Container}
                        {...props}
                        isErrorPage={isErrorPage}
                    />
                </SidebarProvider>
            </CountryProvider>
        </BookmarkProvider>
    );

    Wrapper.propTypes = {
        containerName: PropTypes.string,
        shouldShowSidebar: PropTypes.bool,
        bookmarkedPages: PropTypes.array,
    };

    return Wrapper;
};

Base.propTypes = {
    displayName: PropTypes.string,
};

export default Base;
